import {
  type WorkflowsEdgeInput,
  type WorkflowsNodeInput,
  WorkflowsValueObjectsNodeTypeEnum,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  EdgeUI,
  type NodeUI,
  NodeUIApproval,
  NodeUICondition,
  NodeUIMultiAction,
  NodeUITask,
  NodeUITerminate,
  NodeUITrigger,
  NodeUIType,
  isNodeUIMultiAction,
  isValidActionGroup,
} from 'modules/workflows/components/Node/types'
import { deserializeWorkflowNodes, serializeNodeData } from 'modules/workflows/lib/backend-adapter'
import {
  BackendActionNode,
  BackendActionNodeData,
  BackendEdge,
  BackendNode,
  BackendNodeData,
  BackendForkNode,
  BackendJoinNode,
  BackendStartrNode,
  BackendSwitchComparison,
  BackendSwitchNode,
  BackendSwitchNodeData,
  BackendTerminateNode,
  WorkflowsNode,
  isBackendActionNode,
  isBackendForkNode,
  isBackendJoinNode,
  isBackendStartNode,
  isBackendSwitchNode,
  isBackendTerminateNode,
} from 'modules/workflows/lib/backend-types'
import {
  branchTransitionEdgeId,
  conditionParentEdgeId,
  conditionSplitEdgeId,
  conditionBranchId as makeConditionBranchId,
  normalEdgeId,
} from 'modules/workflows/lib/flow/ids'

const mapToNodeUI = (
  node: BackendNode,
  handlers: Array<{ name: string; actionGroup: string }>
): NodeUI | NodeUI[] => {
  if (isBackendTerminateNode(node)) {
    return mapTerminateNode(node)
  }

  if (isBackendSwitchNode(node)) {
    return mapSwitchNode(node)
  }

  if (isBackendActionNode(node)) {
    return mapActionNode(node, handlers)
  }

  if (isBackendStartNode(node)) {
    return mapStartNode(node)
  }

  throw new Error(`Unknown node type: ${node}`)
}

const mapActionNode = (
  node: BackendActionNode,
  handlers: Array<{ name: string; actionGroup: string }>
): NodeUITask | NodeUIApproval => {
  const handler = handlers.find((h) => h.name === node.data.handler_name)

  if (!handler) {
    throw new Error(
      `Could not find handler: ${node.data.handler_name}. Available handlers: ${handlers
        .map((h) => h.name)
        .join(', ')}`
    )
  }

  if (!isValidActionGroup(handler.actionGroup)) {
    throw new Error(
      `Invalid action_group '${handler.actionGroup}' for handler: ${node.data.handler_name}. Expected 'task' or 'approvals'.`
    )
  }

  const action_group = handler.actionGroup

  return {
    id: node.identifier,
    position: { x: 0, y: 0 },
    data: {
      step: 1,
      title: node.title || i18n.t('workflows.node_type.task.default.title'),
      description: node.description || i18n.t('workflows.node_type.task.default.description'),
      isValid: true,
      payload: node.data.payload,
      handler_name: node.data.handler_name,
      action_group: action_group,
    },
    type: action_group,
  }
}

const mapStartNode = (node: BackendStartrNode): NodeUITrigger => {
  return {
    id: node.identifier,
    position: { x: 0, y: 0 },
    data: {
      step: 1,
      title: node.title || i18n.t('workflows.node_type.trigger.default.title'),
      description: node.description || i18n.t('workflows.node_type.trigger.default.description'),
      isValid: true,
      ...node.data,
    },
    type: 'trigger',
  }
}

const mapTerminateNode = (node: BackendTerminateNode): NodeUITerminate => {
  return {
    id: node.identifier,
    position: { x: 0, y: 0 },
    data: {
      step: 1,
      title: node.title || i18n.t('workflows.node_type.terminate.default.title'),
      description: node.description || i18n.t('workflows.node_type.terminate.default.description'),
      isValid: true,
      workflow_output: node.data.workflow_output,
    },
    type: 'terminate',
  }
}

const createConditionIfNodes = (
  parentNodeId: string,
  comparisons: BackendSwitchComparison[]
): NodeUI[] => {
  return comparisons.map((comparison) => ({
    id: makeConditionBranchId(parentNodeId, `if-${comparison.label}`),
    position: { x: 0, y: 0 },
    data: {
      step: 1,
      title: i18n.t('workflows.node_type.condition_if.title', { label: comparison.label }),
      description: i18n.t('workflows.node_type.condition_if.description'),
      isValid: true,
      comparison,
    },
    type: 'conditionIf' as const,
  }))
}

const createConditionSplitEdges = (
  parentNodeId: string,
  comparisons: BackendSwitchComparison[]
): EdgeUI[] => {
  return comparisons.map((comparison) => ({
    id: conditionSplitEdgeId(parentNodeId, comparison.label),
    source: parentNodeId,
    target: makeConditionBranchId(parentNodeId, `if-${comparison.label}`),
    label: comparison.label,
  }))
}

export const createAllConditionChildNodes = (
  parentNodeId: string,
  comparisons: BackendSwitchComparison[]
): NodeUI[] => {
  const conditionIfNodes = createConditionIfNodes(parentNodeId, comparisons)

  const elseNode: NodeUI = {
    id: makeConditionBranchId(parentNodeId, 'else'),
    position: { x: 0, y: 0 },
    data: {
      step: 1,
      title: i18n.t('workflows.node_type.condition_else.title'),
      description: i18n.t('workflows.node_type.condition_else.description'),
      isValid: true,
    },
    type: 'conditionElse',
  }

  return [...conditionIfNodes, elseNode]
}

export const createAllConditionChildEdges = (
  parentNodeId: string,
  comparisons: BackendSwitchComparison[]
): EdgeUI[] => {
  const conditionIfEdges = createConditionSplitEdges(parentNodeId, comparisons)

  const elseEdge: EdgeUI = {
    id: conditionSplitEdgeId(parentNodeId, 'false'),
    source: parentNodeId,
    target: makeConditionBranchId(parentNodeId, 'else'),
    label: 'default',
  }

  return [...conditionIfEdges, elseEdge]
}

const mapSwitchNode = (node: BackendSwitchNode): NodeUI[] => {
  return [
    {
      id: node.identifier,
      position: { x: 0, y: 0 },
      data: {
        step: 1,
        title: node.title || i18n.t('workflows.node_type.condition.default.title'),
        description:
          node.description || i18n.t('workflows.node_type.condition.default.description'),
        isValid: true,
        comparisons: node.data.comparisons,
      },
      type: 'condition' as const,
    },
    ...createAllConditionChildNodes(node.identifier, node.data.comparisons),
  ]
}

const mapToFlowEdgesForNode = (node: BackendNode): EdgeUI[] => {
  if (!isBackendSwitchNode(node)) return []

  return createAllConditionChildEdges(node.identifier, node.data.comparisons)
}

const mapToFlowEdgesForEdge = (
  edge: BackendEdge,
  getNode: (id: string) => BackendNode | undefined
): EdgeUI[] => {
  const fromId = String(edge.fromNodeIdentifier)
  const toId = String(edge.toNodeIdentifier)
  const from = getNode(fromId)
  const to = getNode(toId)
  if (!from || !to) return []

  if (isBackendSwitchNode(from)) {
    // Try to match the edge label with a comparison label
    const comparison = from.data.comparisons.find((comp) => comp.label === edge.label)
    const branchId = comparison
      ? makeConditionBranchId(from.identifier, `if-${comparison.label}`)
      : makeConditionBranchId(from.identifier, 'else')

    return [
      {
        id: branchTransitionEdgeId(fromId, comparison ? 'true' : 'false', toId),
        source: branchId,
        target: toId,
        label: edge.label ?? undefined,
      },
    ]
  }

  if (to.nodeType === 'switch') {
    return [
      {
        id: conditionParentEdgeId(fromId, toId),
        source: fromId,
        target: toId,
      },
    ]
  }

  return [
    {
      id: normalEdgeId(fromId, toId),
      source: fromId,
      target: toId,
      label: edge.label ?? undefined,
    },
  ]
}

type ParallelBlock = {
  fork: BackendForkNode
  join: BackendJoinNode
  actions: BackendActionNode[]
}

function findParallelBlocks(
  backendNodes: BackendNode[],
  workflowEdges: BackendEdge[]
): ParallelBlock[] {
  const nodesById = new Map(backendNodes.map((n) => [n.identifier, n]))
  const edgesByFrom = workflowEdges.reduce((acc, e) => {
    const from = String(e.fromNodeIdentifier)
    if (!acc.has(from)) acc.set(from, [])
    acc.get(from)?.push(e)
    return acc
  }, new Map<string, BackendEdge[]>())


  const forks = backendNodes.filter((n): n is BackendForkNode => isBackendForkNode(n))
  const blocks: ParallelBlock[] = []

  for (const fork of forks) {
    const outgoing = edgesByFrom.get(fork.identifier) ?? []
    if (outgoing.length < 2) continue

    const targetIds = outgoing.map((e) => String(e.toNodeIdentifier))
    const targetNodes = targetIds
      .map((id) => nodesById.get(id))
      .filter((n): n is BackendActionNode => n != null && isBackendActionNode(n))
    if (targetNodes.length !== targetIds.length) continue

    const joinIds = targetNodes
      .map((a) => {
        const out = edgesByFrom.get(a.identifier) ?? []
        const first = out[0]
        return out.length === 1 && first !== undefined
          ? String(first.toNodeIdentifier)
          : null
      })
      .filter((id): id is string => id != null)
    const uniqueJoinIds = [...new Set(joinIds)]
    const joinId = uniqueJoinIds[0]
    if (uniqueJoinIds.length !== 1 || joinId === undefined) continue

    const joinNode = nodesById.get(joinId)
    if (!joinNode || !isBackendJoinNode(joinNode)) continue

    const joinOutgoing = edgesByFrom.get(joinId) ?? []
    if (joinOutgoing.length !== 1) continue

    const actionIds = new Set(joinNode.data.join_on)
    const expectedActionIds = new Set(targetNodes.map((a) => a.identifier))
    if (actionIds.size !== expectedActionIds.size || [...actionIds].some((id) => !expectedActionIds.has(id))) continue

    blocks.push({ fork, join: joinNode, actions: targetNodes })
  }

  return blocks
}

function createMultiActionNodeFromBlock(
  block: ParallelBlock,
  handlers: Array<{ name: string; actionGroup: string }>
): NodeUIMultiAction {
  const parallelTasks = block.actions.map((action) => {
    const handler = handlers.find((h) => h.name === action.data.handler_name)
    if (!handler || !isValidActionGroup(handler.actionGroup)) {
      throw new Error(
        `Invalid or missing handler for parallel task: ${action.data.handler_name}`
      )
    }
    return {
      id: action.identifier,
      type: handler.actionGroup,
      title: action.title ?? '',
      description: action.description ?? '',
      handler_name: action.data.handler_name,
      payload: action.data.payload ?? {},
      action_group: handler.actionGroup,
    }
  })
  const defaultTitle = i18n.t('workflows.node_type.multi_action.title')
  const defaultDescription = i18n.t('workflows.node_type.multi_action.description')
  const forkId = block.fork.identifier
  const joinId = block.join.identifier
  return {
    id: block.fork.identifier,
    position: { x: 0, y: 0 },
    type: 'multiAction',
    data: {
      step: 1,
      title: block.fork.title ?? defaultTitle,
      description: block.fork.description ?? defaultDescription,
      isValid: true,
      parallelTasks,
      forkIdentifier: forkId,
      joinIdentifier: joinId,
    },
  }
}
// Builds a multiaction node for the UI basd on the data from the fork + join and action nodes from the backend.
function collapseParallelBlocksToMultiAction(
  backendNodes: BackendNode[],
  workflowEdges: BackendEdge[],
  handlers: Array<{ name: string; actionGroup: string }>
): {
  multiActionNodes: NodeUIMultiAction[]
  nodeIdsInBlocks: Set<string>
  blocksByForkId: Map<string, ParallelBlock>
  blocksByJoinId: Map<string, ParallelBlock>
} {
  const blocks = findParallelBlocks(backendNodes, workflowEdges)
  const nodeIdsInBlocks = new Set(
    blocks.flatMap((b) => [
      b.fork.identifier,
      b.join.identifier,
      ...b.actions.map((a) => a.identifier),
    ])
  )
  const multiActionNodes = blocks.map((b) => createMultiActionNodeFromBlock(b, handlers))
  const blocksByForkId = new Map(blocks.map((b) => [b.fork.identifier, b]))
  const blocksByJoinId = new Map(blocks.map((b) => [b.join.identifier, b]))
  return { multiActionNodes, nodeIdsInBlocks, blocksByForkId, blocksByJoinId }
}

// Transform GraphQL server data to UI state
export const mapGraphQLToUI = (
  workflowNodes: WorkflowsNode[],
  workflowEdges: BackendEdge[],
  handlers: Array<{ name: string; actionGroup: string }>,
  nodeDecorator?: (node: NodeUI) => React.ReactNode | undefined
): { nodesUI: NodeUI[]; edgesUI: EdgeUI[] } => {
  const backendNodes = deserializeWorkflowNodes(workflowNodes)
  const { multiActionNodes, nodeIdsInBlocks, blocksByForkId, blocksByJoinId } =
    collapseParallelBlocksToMultiAction(backendNodes, workflowEdges, handlers)

  const nodesById = Object.fromEntries(backendNodes.map((n) => [String(n.identifier), n]))
  const otherNodes = backendNodes.filter(
    (n) =>
      !nodeIdsInBlocks.has(n.identifier) &&
      !isBackendForkNode(n) &&
      !isBackendJoinNode(n)
  )

  const nodesUIFromOthers = otherNodes.flatMap((node) => mapToNodeUI(node, handlers)).flat()
  const nodesUI = [...nodesUIFromOthers, ...multiActionNodes]
    .map((n) => {
      const updatedNode = { ...n, position: { x: 0, y: 0 } }
      updatedNode.data = {
        ...n.data,
        nodeAttachment: nodeDecorator ? nodeDecorator({ ...n, selected: false }) : undefined,
      }
      return updatedNode
    })

  const structural = otherNodes.flatMap((n) => mapToFlowEdgesForNode(n))
  const rawTransitions = workflowEdges.flatMap((e) =>
    mapToFlowEdgesForEdge(e, (id: string) => nodesById[id])
  )
  const transitions = rawTransitions
    .filter((edge) => {
      if (nodeIdsInBlocks.has(edge.source) && nodeIdsInBlocks.has(edge.target)) return false
      return true
    })
    .map((edge) => {
      const sourceBlock = blocksByJoinId.get(edge.source)
      const targetBlock = blocksByForkId.get(edge.target)
      const newSource = sourceBlock ? sourceBlock.fork.identifier : edge.source
      const newTarget = targetBlock ? targetBlock.fork.identifier : edge.target
      return {
        ...edge,
        id: normalEdgeId(newSource, newTarget),
        source: newSource,
        target: newTarget,
      }
    })
  const edgesUI = [...structural, ...transitions]

  return { nodesUI, edgesUI }
}

// Helper function to check if a node is a condition child (should not be sent to backend)
const isConditionUIChild = (node: NodeUI): boolean => {
  return node.type === 'conditionIf' || node.type === 'conditionElse'
}

// Type guards for NodeUI types
const isNodeUITrigger = (node: NodeUI): node is NodeUITrigger => {
  return node.type === 'trigger'
}

const isNodeUICondition = (node: NodeUI): node is NodeUICondition => {
  return node.type === 'condition'
}

const isNodeUIAction = (node: NodeUI): node is NodeUITask | NodeUIApproval => {
  return node.type === 'task' || node.type === 'approvals'
}

const isNodeUITerminate = (node: NodeUI): node is NodeUITerminate => {
  return node.type === 'terminate'
}

// Helper function to extract backend data from NodeUI (not used for multiAction - those are expanded)
const extractBackendData = (node: NodeUI): BackendNodeData => {
  if (isNodeUITrigger(node)) {
    return {} // BackendStartNodeData is an empty object
  }

  if (isNodeUICondition(node)) {
    return {
      _type: 'switch_data',
      comparisons: node.data.comparisons || [],
    } satisfies BackendSwitchNodeData
  }

  if (isNodeUIAction(node)) {
    return {
      _type: 'action_data',
      payload: node.data.payload || {},
      handler_name: node.data.handler_name || '',
    } satisfies BackendActionNodeData
  }

  if (isNodeUITerminate(node)) {
    return {
      workflow_output: node.data.workflow_output || {},
    }
  }

  if (node.type === 'multiAction') {
    throw new Error('multiAction nodes are expanded in serializeNodesForMutation, not extracted')
  }

  throw new Error(`Cannot extract backend data from unknown UI node type: ${node.type}`)
}

// Helper function to map UI node types to backend node types (multiAction is expanded, not mapped)
const mapUITypeToBackendType = (uiType: NodeUIType): WorkflowsValueObjectsNodeTypeEnum => {
  switch (uiType) {
    case 'trigger':
      return 'start'
    case 'condition':
      return 'switch'
    case 'task':
    case 'approvals':
      return 'action'
    case 'terminate':
      return 'terminate'
      // Multiaction node is called from the UI perspective but this does not have a backend equivalent. This gets expanded into Join and Fork nodes in serializeNodesForMutation.
    case 'multiAction':
      throw new Error('multiAction nodes are expanded in serializeNodesForMutation, not mapped')
    default:
      throw new Error(`Unknown UI node type: ${uiType}`)
  }
}

function expandMultiActionToBackendNodes(ma: NodeUIMultiAction): WorkflowsNodeInput[] {
  const forkId = ma.data.forkIdentifier ?? ma.id
  const joinId = ma.data.joinIdentifier ?? `${ma.id}_join`
  const forkNode: WorkflowsNodeInput = {
    identifier: forkId,
    nodeType: 'fork',
    data: JSON.stringify({ _type: 'fork_data' }),
    title: ma.data.title ?? null,
    description: ma.data.description ?? null,
  }
  const joinNode: WorkflowsNodeInput = {
    identifier: joinId,
    nodeType: 'join',
    data: JSON.stringify({
      _type: 'join_data',
      join_on: ma.data.parallelTasks.map((t) => t.id),
    }),
    title: null,
    description: null,
  }
  const actionNodes: WorkflowsNodeInput[] = ma.data.parallelTasks.map((task) => ({
    identifier: task.id,
    nodeType: 'action',
    data: serializeNodeData({
      _type: 'action_data',
      payload: task.payload,
      handler_name: task.handler_name,
    }),
    title: task.title ?? null,
    description: task.description ?? null,
  }))
  // Emit action nodes before the join so the backend sees their identifiers when it
  // validates the join's join_on (validation runs during each node's create!/update!).
  return [forkNode, ...actionNodes, joinNode]
}

// Serialization functions for mutations
export const serializeNodesForMutation = (nodes: NodeUI[]): WorkflowsNodeInput[] => {
  const regularNodes = nodes.filter(
    (node) => !isConditionUIChild(node) && node.type !== 'multiAction'
  )
  const multiActionNodes = nodes.filter((node): node is NodeUIMultiAction =>
    isNodeUIMultiAction(node)
  )

  const regularBackendNodes = regularNodes.map((node) => {
    const backendData = extractBackendData(node)
    return {
      identifier: node.id,
      nodeType: mapUITypeToBackendType(node.type),
      data: serializeNodeData(backendData),
      title: node.data.title || null,
      description: node.data.description || null,
    }
  })
  const expandedNodes = multiActionNodes.flatMap(expandMultiActionToBackendNodes)

  return [...regularBackendNodes, ...expandedNodes]
}

export const serializeEdgesForMutation = (
  edges: EdgeUI[],
  nodes: NodeUI[]
): WorkflowsEdgeInput[] => {
  const nodeMap = new Map(nodes.map((node) => [node.id, node]))
  const multiActionNodes = nodes.filter((node): node is NodeUIMultiAction =>
    isNodeUIMultiAction(node)
  )

  // Pre-compute outoging edges of condition children by source
  const childEdgesBySource = edges
    .filter((edge) => {
      const sourceNode = nodeMap.get(edge.source)
      return sourceNode && isConditionUIChild(sourceNode)
    })
    .reduce((acc, edge) => {
      const existing = acc.get(edge.source) || []
      //accumulate edges of each children by source
      acc.set(edge.source, [...existing, edge])
      return acc
    }, new Map<string, EdgeUI[]>())

  const mapSource = (id: string): string => {
    const node = nodeMap.get(id)
    if (node?.type === 'multiAction') {
      return node.data.joinIdentifier ?? `${id}_join`
    }
    return id
  }
  const mapTarget = (id: string): string => {
    const node = nodeMap.get(id)
    return node?.type === 'multiAction' ? (node.data.forkIdentifier ?? id) : id
  }

  const externalEdges = edges.flatMap((edge) => {
    const source = nodeMap.get(edge.source)
    const target = nodeMap.get(edge.target)

    // Skip internal structure edges (condition → condition children)
    // but convert them to workflow transitions by following the child's outgoing edges
    if (source && isNodeUICondition(source) && target && isConditionUIChild(target)) {
      const childEdges = childEdgesBySource.get(edge.target) || []
      return childEdges.map((childEdge) => ({
        fromIdentifier: mapSource(edge.source),
        toIdentifier: mapTarget(childEdge.target),
        label: edge.label?.toString() ?? null,
      }))
    }

    // Skip edges from condition children (they're handled above)
    if (source && isConditionUIChild(source)) {
      return []
    }

    return [
      {
        fromIdentifier: mapSource(edge.source),
        toIdentifier: mapTarget(edge.target),
        label: edge.label?.toString() ?? null,
      },
    ]
  })

  const internalEdges = multiActionNodes.flatMap((ma) => {
    const forkId = ma.data.forkIdentifier ?? ma.id
    const joinId = ma.data.joinIdentifier ?? `${ma.id}_join`
    const taskIds = ma.data.parallelTasks.map((t) => t.id)
    const forkToTasks: WorkflowsEdgeInput[] = taskIds.map((toId) => ({
      fromIdentifier: forkId,
      toIdentifier: toId,
      label: null,
    }))
    const tasksToJoin: WorkflowsEdgeInput[] = taskIds.map((fromId) => ({
      fromIdentifier: fromId,
      toIdentifier: joinId,
      label: null,
    }))
    return [...forkToTasks, ...tasksToJoin]
  })

  return [...externalEdges, ...internalEdges]
}
